// =============================================================================
// i18n - English / Spanish for the patient portal and the check-in kiosk.
//
// Keys are the English text itself: t("Sign out"). A string with no Spanish
// entry shows in English, so nothing ever renders blank. The staff app has no
// LangProvider and always gets English from the default context, which is why
// shared components (the HRSN screener, the telehealth button) can call t()
// safely.
//
//   const { t, tn, lang, locale, setLang } = useT();
//   t("Hello, {name}", { name })            // {placeholders} are filled in
//   tn(count, "{n} new message", "{n} new messages")
//   new Date(x).toLocaleDateString(locale, { month: "short", day: "numeric" })
//
// Text kept in constants outside a component is wrapped in tk("...") (returns
// the text unchanged) so scripts/check-i18n.mjs can find it, then translated
// where it renders: t(item.label).
//
// Spanish lives in src/lib/i18n/es.js. The codebase is ASCII only, so accents
// there are \u escapes. scripts/check-i18n.mjs fails the build when a t() key
// has no Spanish or a portal / kiosk screen shows raw English text.
// =============================================================================
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { C } from "./tokens";

// The Spanish dictionary (~30 KB gzipped) loads only when Spanish is in use,
// so staff and English-speaking patients never download it. Until it arrives
// (a moment on first switch) text shows in English.
const SUPPORTED = ["en", "es"];
const DICTS = {};
let esPromise = null;
function loadSpanish() {
  if (!esPromise) {
    esPromise = import("./i18n/es.js").then((m) => { DICTS.es = m.default; return m.default; });
    esPromise.catch(() => { esPromise = null; });
  }
  return esPromise;
}
export const LANGS = [
  { code: "en", label: "English", locale: "en-US" },
  { code: "es", label: "Espa\u00f1ol", locale: "es-US" },
];
const LOCALES = { en: "en-US", es: "es-US" };

function fill(s, vars) {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k])));
}

// A key may carry a context after "|" when one English word needs different
// Spanish in different places: t("None|appointment") shows "None" in English
// and "Ninguna" (feminine, for "cita") in Spanish.
export function translate(lang, key, vars) {
  if (key === null || key === undefined) return key;
  const d = DICTS[lang];
  const hit = d && Object.prototype.hasOwnProperty.call(d, key) ? d[key] : null;
  const s = hit || (typeof key === "string" && key.includes("|") ? key.slice(0, key.indexOf("|")) : key);
  return fill(String(s), vars);
}

// Marks text for extraction without translating it (constants at module scope).
export function tk(s) { return s; }

// patients.preferred_language holds free text ("Spanish", "spanish", "es",
// "Espanol"). Returns "es", "en" or null when it is another language.
export function langFromPreferred(pref) {
  if (!pref) return null;
  const p = String(pref).trim().toLowerCase();
  if (p === "es" || p.startsWith("span") || p.startsWith("espa")) return "es";
  if (p === "en" || p.startsWith("eng")) return "en";
  return null;
}

function readStored(key) {
  if (!key) return null;
  try { const v = localStorage.getItem(key); return SUPPORTED.includes(v) ? v : null; } catch { return null; }
}
function writeStored(key, v) {
  if (!key) return;
  try { localStorage.setItem(key, v); } catch { /* private mode */ }
}

function browserLang() {
  try {
    const list = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
    return String(list[0] || "").toLowerCase().startsWith("es") ? "es" : "en";
  } catch { return "en"; }
}

function makeApi(lang, setLang) {
  const t = (key, vars) => translate(lang, key, vars);
  const tn = (n, one, other, vars) => translate(lang, n === 1 ? one : other, { n, ...(vars || {}) });
  return { lang, locale: LOCALES[lang] || "en-US", t, tn, setLang };
}

const LangContext = createContext(makeApi("en", () => {}));

export function useT() { return useContext(LangContext); }

// storageKey: where a patient's explicit choice is kept on this device
//   (portal). Pass null on shared devices (kiosk) so a choice never carries
//   over to the next patient.
// preferred: patients.preferred_language, used until the user picks a language.
// defaultLang: "en", "es", or "auto" (the browser's language; used on the
//   sign-in and activation screens, before we know who the patient is).
// resetKey: changing it drops back to the default (kiosk "start over").
export function LangProvider({ storageKey = null, preferred = null, defaultLang = "en", resetKey, children }) {
  const [chosen, setChosen] = useState(() => readStored(storageKey));
  const [picked, setPicked] = useState(null);
  useEffect(() => { setPicked(null); }, [resetKey]);

  const fallback = defaultLang === "auto" ? browserLang() : defaultLang;
  const lang = picked || chosen || langFromPreferred(preferred) || fallback;

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setPicked(next);
    if (storageKey) { setChosen(next); writeStored(storageKey, next); }
  }, [storageKey]);

  const [loaded, setLoaded] = useState(0);
  useEffect(() => {
    if (lang !== "es" || DICTS.es) return undefined;
    let live = true;
    loadSpanish().then(() => { if (live) setLoaded((n) => n + 1); }).catch(() => { /* stays English; retried on next switch */ });
    return () => { live = false; };
  }, [lang]);

  // Screen readers pick their voice from <html lang>.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const el = document.documentElement;
    const before = el.getAttribute("lang");
    el.setAttribute("lang", lang);
    return () => { if (before) el.setAttribute("lang", before); else el.removeAttribute("lang"); };
  }, [lang]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const api = useMemo(() => makeApi(lang, setLang), [lang, setLang, loaded]);
  return <LangContext.Provider value={api}>{children}</LangContext.Provider>;
}

// English | Espanol switch. `compact` for tight headers. tone="dark" for dark
// surfaces (sign-in card) where the unselected button must stay legible.
export function LanguageSwitch({ compact = false, style, tone }) {
  const { lang, setLang, t } = useT();
  const dark = tone === "dark";
  return (
    <div role="group" aria-label={t("Language")} style={{ display: "inline-flex", gap: 4, ...(style || {}) }}>
      {LANGS.map((l) => {
        const on = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            lang={l.code}
            aria-pressed={on}
            onClick={() => setLang(l.code)}
            style={{
              padding: compact ? "4px 10px" : "8px 16px",
              fontSize: compact ? 12 : 16,
              fontWeight: on ? 700 : 500,
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: "inherit",
              /* dark-mode-ignore-start: tone="dark" is for the always-dark sign-in card; light tone uses tokens */
              border: "1px solid " + (on ? "var(--brand-primary, #0F6E56)" : (dark ? "rgba(255,255,255,0.22)" : C.borderMid)),
              background: on ? "var(--brand-primary-accessible, var(--brand-primary, #0F6E56))" : "transparent",
              color: on ? (dark ? "#ffffff" : C.onAccent) : (dark ? "#d4d4d4" : "inherit"),
              /* dark-mode-ignore-end */
            }}
          >{l.label}</button>
        );
      })}
    </div>
  );
}
